import type {
  ResultUIStudentAcademicRisk,
  UIFilterAcademicRisk,
} from '@/domains/academicRisk/academicRiskStudent.entity';
import type { FilterSelection } from '@/domains/shared';

/**
 * Use Case para filtrar estudiantes de riesgo académico a nivel UI
 *
 * Responsabilidades:
 * - Filtrar por búsqueda textual (nombre, RUN, sede, estado, riesgos)
 * - Filtrar por jornada seleccionada
 * - Filtrar por centro seleccionado
 * - Filtrar por indicador de riesgo activo
 */
export class FilterAcademicRiskUIUseCase {
  /**
   * Aplica filtro de búsqueda textual sobre múltiples campos
   * Busca coincidencias en nombre, RUN, sede, estado y riesgos identificados
   * @param students - Array de estudiantes a filtrar
   * @param searchQuery - Texto de búsqueda (opcional, case-insensitive)
   */
  private applySearchFilter(
    students: ResultUIStudentAcademicRisk[],
    searchQuery?: string,
  ): ResultUIStudentAcademicRisk[] {
    if (!searchQuery || searchQuery.trim() === '') return students;

    const searchLower = searchQuery.toLowerCase();
    return students.filter((student) => {
      return (
        student.nombre.toLowerCase().includes(searchLower) ||
        student.run.toLowerCase().includes(searchLower) ||
        student.sede.toLowerCase().includes(searchLower) ||
        student.estado.toLowerCase().includes(searchLower) ||
        student.riesgos_identificados.toLowerCase().includes(searchLower)
      );
    });
  }

  /**
   * Aplica filtro de jornada sobre estudiantes
   * @param students - Array de estudiantes a filtrar
   * @param jornadaFilter - Jornadas seleccionadas ('all' para todas, array para específicas)
   */
  private applyJornadaFilter(
    students: ResultUIStudentAcademicRisk[],
    jornadaFilter?: FilterSelection,
  ): ResultUIStudentAcademicRisk[] {
    if (!jornadaFilter || jornadaFilter === 'all' || jornadaFilter.size === 0) {
      return students;
    }

    return students.filter((student) => jornadaFilter.has(student.jornada));
  }

  /**
   * Aplica filtro de centro (sede) sobre estudiantes
   * @param students - Array de estudiantes a filtrar
   * @param centerFilter - Centros seleccionados ('all' para todos, array para específicos)
   */
  private applyCenterFilter(
    students: ResultUIStudentAcademicRisk[],
    centerFilter?: FilterSelection,
  ): ResultUIStudentAcademicRisk[] {
    if (!centerFilter || centerFilter === 'all' || centerFilter.size === 0)
      return students;

    return students.filter((student) => centerFilter.has(student.cod_sede));
  }

  /**
   * Aplica filtro de indicador de riesgo específico
   * Filtra estudiantes que tengan el indicador de riesgo activo en su riskArray
   * @param students - Array de estudiantes a filtrar
   * @param activeFilter - Nombre del indicador de riesgo activo (case-insensitive)
   */
  private applyRiskIndicatorFilter(
    students: ResultUIStudentAcademicRisk[],
    activeFilter?: string,
  ): ResultUIStudentAcademicRisk[] {
    if (!activeFilter || activeFilter.trim() === '') return students;

    return students.filter((student) => {
      return student.riskArray.some(
        (risk) => risk.toLowerCase() === activeFilter.toLowerCase(),
      );
    });
  }

  /**
   * Ejecuta el filtrado de estudiantes aplicando todos los filtros de UI en secuencia
   * Orden de aplicación: 1) Búsqueda, 2) Jornada, 3) Centro, 4) Indicador de riesgo
   * @param students - Array completo de estudiantes a filtrar
   * @param uiFilters - Objeto con todos los filtros de UI a aplicar
   */
  execute(
    students: ResultUIStudentAcademicRisk[],
    uiFilters: UIFilterAcademicRisk,
  ): ResultUIStudentAcademicRisk[] {
    if (!students || students.length === 0) return [];

    let filteredStudents = [...students];

    // 1. Aplicar búsqueda por texto
    filteredStudents = this.applySearchFilter(
      filteredStudents,
      uiFilters.searchQuery,
    );

    // 2. Aplicar filtro de jornada
    filteredStudents = this.applyJornadaFilter(
      filteredStudents,
      uiFilters.jornadaFilter,
    );

    // 3. Aplicar filtro de centro
    filteredStudents = this.applyCenterFilter(
      filteredStudents,
      uiFilters.centerFilter,
    );

    // 4. Aplicar filtro de indicador de riesgo
    filteredStudents = this.applyRiskIndicatorFilter(
      filteredStudents,
      uiFilters.activeFilter,
    );

    return filteredStudents;
  }
}
